import React from "react";

import {
  List,
  DataTable,
  TextField,
  NumberField,
  ReferenceField,
  SimpleForm,
  Create,
  Edit,
  Show,
  TabbedShowLayout,
  Tab,
  ReferenceInput,
  Pagination,
  useRecordContext,
  required,
  NumberInput,
  AutocompleteInput,
  TopToolbar,
  ColumnsButton,
  CreateButton,
  FilterButton,
} from "react-admin";
import { CustomDatetimeField, CustomDateTimeInput } from "../lib/datetime";
import { DetailsButton } from "../lib/detailsButton";
import { CustomTab } from "../lib/customTab";
import { AliasOrBarcodeReference } from "../lib/aliasWithBarcodeTooltip";
import { hasScope, SCOPE } from "../lib/auth";

export const AnalysisList = () => (
  <List
    perPage={100}
    sort={{ field: "timestamp", order: "DESC" }}
    pagination={<Pagination rowsPerPageOptions={[100, 200, 500, 1000]} />}
    filters={[
      <ReferenceInput label="Lot" source="lotID" reference="lots" perPage={100}>
        <AutocompleteInput
          optionText="name"
          label="Lot"
          filterToQuery={(searchText) => ({ search: searchText })}
          style={{ minWidth: 300 }}
        />
      </ReferenceInput>,
      <ReferenceInput
        label="Vessel"
        source="vesselID"
        reference="vessels"
        perPage={100}
      >
        <AutocompleteInput
          optionText={(rec) => (rec.alias ? rec.alias : rec.barcode)}
          filterToQuery={(searchText) => ({ search: searchText })}
          label="Vessel"
          style={{ minWidth: 300 }}
        />
      </ReferenceInput>,
      <ReferenceInput
        label="Analysis Type"
        source="analysisTypeID"
        reference="analysis_types"
        perPage={100}
      >
        <AutocompleteInput
          optionText={(rec) => `${rec.analyte} (${rec.unit})`}
          label="Analysis Type"
          style={{ minWidth: 300 }}
        />
      </ReferenceInput>,
    ]}
    actions={
      <TopToolbar>
        <FilterButton />
        <CreateButton disabled={!hasScope(SCOPE.MANAGER)} />
        <ColumnsButton />
      </TopToolbar>
    }
    storeKey={false}
    empty={false}
  >
    <DataTable bulkActionButtons={false} rowClick={false}>
      <DataTable.Col source="timestamp" label="Timestamp">
        <CustomDatetimeField source="timestamp" label="Timestamp" />
      </DataTable.Col>
      <DataTable.Col source="analyte" label="Analyte" disableSort>
        <ReferenceField
          source="analysisTypeID"
          reference="analysis_types"
          sortable={false}
        >
          <TextField source="analyte" />
        </ReferenceField>
      </DataTable.Col>
      <DataTable.Col source="value" label="Value" disableSort>
        <NumberField source="value" label="Value" />
      </DataTable.Col>
      <DataTable.Col source="unit" label="Unit" disableSort>
        <ReferenceField
          source="analysisTypeID"
          reference="analysis_types"
          sortable={false}
          link={false}
        >
          <TextField source="unit" />
        </ReferenceField>
      </DataTable.Col>
      <DataTable.Col source="method" label="Method" disableSort>
        <ReferenceField
          source="analysisTypeID"
          reference="analysis_types"
          sortable={false}
          link={false}
        >
          <TextField source="method" />
        </ReferenceField>
      </DataTable.Col>
      <DataTable.Col source="lotID" label="Lot" disableSort>
        <ReferenceField source="lotID" reference="lots" sortable={false}>
          <TextField source="name" />
        </ReferenceField>
      </DataTable.Col>
      <DataTable.Col source="vesselID" label="Vessel" disableSort>
        <AliasOrBarcodeReference
          source="vesselID"
          reference="vessels"
          label="Vessel"
        />
      </DataTable.Col>
      <DataTable.Col>
        <DetailsButton />
      </DataTable.Col>
    </DataTable>
  </List>
);

const AnalysisEditTitle = () => {
  const record = useRecordContext();
  if (!record) return null;
  return <span>Analysis {record.id}</span>;
};

const AnalysisCreateEditForm = () => (
  <SimpleForm>
    <ReferenceInput
      label="Analysis Type"
      source="analysisTypeID"
      reference="analysis_types"
      perPage={100}
    >
      <AutocompleteInput
        optionText={(rec) => `${rec.analyte} (${rec.unit})`}
        label="Analysis Type"
        validate={required()}
        style={{ minWidth: 300 }}
      />
    </ReferenceInput>
    <NumberInput label="Value" source="value" validate={required()} />
    <ReferenceInput source="lotID" reference="lots">
      <AutocompleteInput
        optionText="name"
        filterToQuery={(searchText) => ({ search: searchText })}
        label="Lot"
        validate={required()}
        style={{ minWidth: 300 }}
      />
    </ReferenceInput>
    <ReferenceInput source="vesselID" reference="vessels">
      <AutocompleteInput
        optionText={(rec) => (rec.alias ? rec.alias : rec.barcode)}
        filterToQuery={(searchText) => ({ search: searchText })}
        label="Vessel"
        style={{ minWidth: 300 }}
      />
    </ReferenceInput>

    <CustomDateTimeInput label="Timestamp" source="timestamp" />
  </SimpleForm>
);

export const AnalysisEdit = () => (
  <Edit title={<AnalysisEditTitle />}>
    <AnalysisCreateEditForm />
  </Edit>
);

export const AnalysisCreate = () => (
  <Create redirect="list">
    <AnalysisCreateEditForm />
  </Create>
);

const AnalysisShowTitle = () => {
  const record = useRecordContext();
  if (!record) return null;
  return <span>Analysis {record.id}</span>;
};

export const AnalysisShow = () => (
  <Show title={<AnalysisShowTitle />}>
    <TabbedShowLayout>
      <Tab label="Overview">
        <TextField source="id" label="ID" />
        <ReferenceField
          label="Analysis Type"
          source="analysisTypeID"
          reference="analysis_types"
          link="show"
        >
          <TextField source="analyte" />
          <TextField source="unit" />
          <TextField source="method" />
        </ReferenceField>
        <NumberField source="value" label="Value" />
        <ReferenceField label="Lot" source="lotID" reference="lots" link="show">
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField
          label="Vessel"
          source="vesselID"
          reference="vessels"
          link="show"
        >
          <TextField source="alias" emptyText={"\u00A0"} />
        </ReferenceField>
        <CustomDatetimeField source="timestamp" label="Timestamp" />
      </Tab>
      <CustomTab />
    </TabbedShowLayout>
  </Show>
);
